/**
 * AgentInbox — floating draggable notification widget.
 *
 * Owner brief Feb 2026: "a little message box somewhere that isn't
 * overlapping anything, agent says 'I've noticed you have received
 * an email...I'm able to fix this for you if you'd like — accept /
 * yes fix it / reply back to it'."
 *
 * Uses DraggableFloater so it can be moved anywhere on screen. When
 * unread notifications arrive, the pulse ring animates + the agent's
 * voice speaks the title once. Click to expand → Accept/Reply/Fix/Dismiss.
 * Polls /api/agent_inbox?status=unread every 15s.
 */
import React, { useEffect, useRef, useState } from "react";
import DraggableFloater from "./DraggableFloater";
import api from "@/lib/api";

const T = {
  bg: "#050b18",
  panel: "rgba(7,17,33,0.94)",
  border: "rgba(34,211,238,0.32)",
  text: "#e6f3ff",
  textDim: "#7897b8",
  cyan: "#22d3ee",
  amber: "#f59e0b",
};

async function speakOnce(text, voiceId) {
  if (!text) return;
  try {
    // ⚠ Backend /jarvis/tts is POST + auth-only, so we can't just
    // `new Audio(GET url)` (that returns 405). Use the authenticated
    // api client, get a base64 data URL, then play it. If ElevenLabs
    // is unavailable, `audio` is null and we fall back to the browser's
    // SpeechSynthesis so the owner still hears something.
    const body = { text: text.slice(0, 300) };
    if (voiceId) body.voice_id = voiceId;
    const r = await api.post("/jarvis/tts", body);
    const dataUrl = r?.data?.audio;
    if (dataUrl) {
      const audio = new Audio(dataUrl);
      audio.volume = 0.9;
      await audio.play().catch(() => {});
    } else if (typeof window !== "undefined" && window.speechSynthesis) {
      const u = new window.SpeechSynthesisUtterance(text);
      u.lang = "en-GB";
      window.speechSynthesis.speak(u);
    }
  } catch { /* ignore */ }
}

// Owner brief Feb 2026: when a new email/improvement arrives, the agent
// should ONLY whisper "Lee, mate — are you there?" (never read the
// content unprompted). Then when the owner says "hi mate" (wake word)
// AND there are unread notifications, the agent greets with a summary
// asking if it should read them out.
const OWNER_NAME = "Lee";
const AreYouThereLines = [
  `${OWNER_NAME} mate, are you there?`,
  `${OWNER_NAME}, are you around, mate?`,
  `Hey ${OWNER_NAME} — you about?`,
];
function pickAreYouThere() {
  return AreYouThereLines[Math.floor(Math.random() * AreYouThereLines.length)];
}
// 🚨 Priority senders get an urgent shout instead of a whisper.
function priorityShout(label) {
  const who = label || "one of your priority contacts";
  return `${OWNER_NAME}! Priority from ${who} — are you there?`;
}
function greetingSummary(list) {
  const priority = list.filter(n => n.priority).length;
  const emails = list.filter(n => n.source === "email" && !n.priority).length;
  const improvements = list.filter(n => n.source === "app_improvement" && !n.priority).length;
  const other = list.length - emails - improvements - priority;
  const parts = [];
  if (priority) parts.push(`${priority} PRIORITY message${priority === 1 ? "" : "s"}`);
  if (emails) parts.push(`${emails} email${emails === 1 ? "" : "s"}`);
  if (improvements) parts.push(`${improvements} possible ${improvements === 1 ? "fix or enhancement" : "fixes or enhancements"}`);
  if (other) parts.push(`${other} other note${other === 1 ? "" : "s"}`);
  if (!parts.length) return "";
  const joined = parts.length === 1 ? parts[0]
    : parts.length === 2 ? `${parts[0]} and ${parts[1]}`
    : `${parts.slice(0, -1).join(", ")}, and ${parts[parts.length - 1]}`;
  return `Hi mate — I've got ${joined} waiting. Want me to read them out?`;
}

export default function AgentInbox() {
  const [notifs, setNotifs] = useState([]);
  const [expanded, setExpanded] = useState(false);
  const [replying, setReplying] = useState(null);   // notif_id being replied to
  const [replyText, setReplyText] = useState("");
  const [drafting, setDrafting] = useState(null);   // notif_id currently drafting
  const [readingOut, setReadingOut] = useState(false);
  // 📌 Persist "already-whispered" IDs to localStorage so a page reload
  // doesn't cause Sarah to re-announce the same 3 stale emails as if new.
  const spokenIdsRef = useRef(
    (() => {
      try {
        const raw = typeof window !== "undefined" && window.localStorage.getItem("rawco-inbox-spoken-ids");
        return new Set(raw ? JSON.parse(raw) : []);
      } catch { return new Set(); }
    })()
  );
  const rememberSpoken = (id) => {
    spokenIdsRef.current.add(id);
    try {
      window.localStorage.setItem(
        "rawco-inbox-spoken-ids",
        JSON.stringify(Array.from(spokenIdsRef.current).slice(-120))
      );
    } catch { /* ignore */ }
  };

  const pull = async () => {
    try {
      const r = await api.get("/agent_inbox?status=unread&limit=20");
      const list = r.data?.notifications || [];
      setNotifs(list);
      // 🚨 Priority senders shout urgently; otherwise fall through to
      // the normal "are you there?" whisper for un-triaged items.
      const priorityNew = list.find(n => n.priority && !spokenIdsRef.current.has(n.id));
      if (priorityNew) {
        rememberSpoken(priorityNew.id);
        if (typeof document !== "undefined" && document.visibilityState !== "hidden") {
          speakOnce(priorityShout(priorityNew.priority_label), priorityNew.voice_id);
        }
      } else {
        const newest = list.find(n => n.speak && !n.triage_bucket && !spokenIdsRef.current.has(n.id));
        if (newest) {
          rememberSpoken(newest.id);
          if (typeof document !== "undefined" && document.visibilityState !== "hidden") {
            speakOnce(pickAreYouThere(), newest.voice_id);
          }
        }
      }
    } catch { /* ignore */ }
  };
  useEffect(() => {
    pull();
    const id = setInterval(pull, 15000);
    return () => clearInterval(id);
  }, []);

  // 🎤 Owner said the wake word ("hi mate" / "raw company" / etc.). If we
  // have unread notifications, greet them with a summary asking if they'd
  // like them read out. Cooldown 90s so re-waking mid-conversation doesn't
  // re-trigger the greeting.
  const lastWakeGreetAtRef = useRef(0);
  useEffect(() => {
    const onWake = () => {
      const now = Date.now();
      if (now - lastWakeGreetAtRef.current < 90000) return;
      // Auto-triaged "info" notifs stay silent — only alert-worthy ones
      // count for the wake greeting.
      const alert = notifs.filter(n => !n.triage_bucket);
      if (!alert.length) return;
      lastWakeGreetAtRef.current = now;
      const line = greetingSummary(alert);
      if (!line) return;
      const voice = alert[0]?.voice_id || "";
      setTimeout(() => speakOnce(line, voice), 1600);
    };
    window.addEventListener("rawco-wake-detected", onWake);
    return () => window.removeEventListener("rawco-wake-detected", onWake);
  }, [notifs]);

  const act = async (id, verb, extra = {}) => {
    try {
      if (verb === "reply") {
        if (!replyText.trim()) return;
        await api.post(`/agent_inbox/${id}/reply`, { text: replyText });
      } else {
        await api.post(`/agent_inbox/${id}/${verb}`, extra);
      }
      setReplying(null);
      setReplyText("");
      await pull();
    } catch (e) {
      console.warn("[agent_inbox]", verb, "failed:", e?.message);
    }
  };

  const draftReply = async (id) => {
    setDrafting(id);
    try {
      const r = await api.post(`/agent_inbox/${id}/draft_reply`, {});
      const draft = r.data?.draft || "";
      setReplying(id);
      setReplyText(draft);
    } catch (e) {
      console.warn("[agent_inbox] draft_reply failed:", e?.message);
    } finally {
      setDrafting(null);
    }
  };

  // 🔊 Sequential read-out: each agent speaks their own notification in
  // their own voice, one after another. Cancellable by tapping again.
  const readingRef = useRef(false);
  const readOutAll = async () => {
    if (readingRef.current) { readingRef.current = false; setReadingOut(false); return; }
    readingRef.current = true;
    setReadingOut(true);
    try {
      for (const n of notifs) {
        if (!readingRef.current) break;
        const line = `${n.agent_name} here. ${n.title}.`;
        await speakOnce(line, n.voice_id);
        // Space between voices so they don't stomp on each other
        await new Promise(r => setTimeout(r, 600));
      }
    } finally {
      readingRef.current = false;
      setReadingOut(false);
    }
  };

  const unreadCount = notifs.length;
  const top = notifs[0];

  return (
    <DraggableFloater
      storageKey="rawco-agent-inbox-pos"
      initial={{ right: 24, bottom: 420 }}
      zIndex={44}
      testid="agent-inbox-floater"
    >
      {!expanded && (
        <button
          type="button"
          onClick={() => setExpanded(true)}
          data-testid="agent-inbox-bubble"
          aria-label="Open agent inbox"
          className="relative"
          style={{
            width: 56, height: 56,
            background: unreadCount > 0 ? "rgba(34,211,238,0.16)" : "rgba(7,17,33,0.72)",
            border: `1.5px solid ${unreadCount > 0 ? T.cyan : T.textDim}`,
            color: T.text,
            cursor: "pointer",
            borderRadius: 999,
            fontSize: 22,
            filter: unreadCount > 0 ? `drop-shadow(0 0 14px ${T.cyan}88)` : "none",
          }}
        >
          {unreadCount > 0 ? "📮" : "📭"}
          {unreadCount > 0 && (
            <span
              data-testid="agent-inbox-badge"
              style={{
                position: "absolute", top: -4, right: -4,
                background: T.amber, color: T.bg,
                borderRadius: 999, minWidth: 20, height: 20,
                fontSize: 11, fontWeight: 800,
                display: "flex", alignItems: "center", justifyContent: "center",
                padding: "0 6px",
              }}
            >{unreadCount > 9 ? "9+" : unreadCount}</span>
          )}
        </button>
      )}

      {expanded && (
        <div
          data-testid="agent-inbox-panel"
          style={{
            width: 360,
            maxHeight: 480,
            background: T.panel,
            border: `1px solid ${T.border}`,
            borderRadius: 10,
            color: T.text,
            display: "flex",
            flexDirection: "column",
            backdropFilter: "blur(12px)",
          }}
        >
          <div className="flex items-center justify-between px-3 py-2"
               style={{ borderBottom: `1px solid ${T.border}` }}>
            <div className="text-[10px] uppercase tracking-widest" style={{ color: T.cyan }}>
              Agent Inbox · {unreadCount} unread
            </div>
            <div className="flex items-center gap-2">
              {unreadCount > 0 && (
                <button
                  data-testid="inbox-read-all"
                  onClick={readOutAll}
                  aria-pressed={readingOut}
                  className="text-[10px] px-2 py-1 uppercase tracking-wider"
                  style={{
                    background: readingOut ? T.cyan : "transparent",
                    border: `1px solid ${T.cyan}`,
                    color: readingOut ? T.bg : T.cyan,
                    borderRadius: 4,
                    cursor: "pointer",
                  }}
                >{readingOut ? "◼ Stop" : "🔊 Read all"}</button>
              )}
              <button
                data-testid="agent-inbox-close"
                onClick={() => { setExpanded(false); setReplying(null); }}
                style={{ background: "transparent", border: "none", color: T.textDim, cursor: "pointer", fontSize: 16 }}
                aria-label="Close"
              >✕</button>
            </div>
          </div>

          <div style={{ overflowY: "auto", flex: 1 }}>
            {notifs.length === 0 && (
              <div className="text-[11px] py-6 text-center" style={{ color: T.textDim }}>
                All caught up. Agents will drop a note here when they spot something.
              </div>
            )}
            {notifs.map(n => (
              <div key={n.id} className="px-3 py-3"
                   data-testid={`inbox-notif-${n.id}`}
                   style={{ borderBottom: `1px solid ${T.border}` }}>
                <div className="flex items-start gap-2 mb-1">
                  <div style={{
                    width: 28, height: 28, borderRadius: 999,
                    background: T.cyan, color: T.bg,
                    display: "flex", alignItems: "center", justifyContent: "center",
                    fontWeight: 700, fontSize: 12, flexShrink: 0,
                  }}>{n.avatar || "?"}</div>
                  <div style={{ flex: 1, minWidth: 0 }}>
                    <div className="text-[11px] font-bold flex items-center gap-1" style={{ color: T.text }}>
                      {n.agent_name}
                      {n.priority && (
                        <span
                          data-testid={`inbox-priority-badge-${n.id}`}
                          title={`Priority: ${n.priority_label || "urgent"}`}
                          style={{
                            fontSize: 9, padding: "1px 5px", borderRadius: 3,
                            background: "rgba(239,68,68,0.18)",
                            color: "#ef4444", border: "1px solid #ef4444",
                            fontWeight: 700, letterSpacing: "0.05em",
                            animation: "pulse 1.6s ease-in-out infinite",
                          }}
                        >🚨 PRIORITY</span>
                      )}
                      {n.triage_bucket && (
                        <span
                          data-testid={`inbox-triage-badge-${n.id}`}
                          title={`Auto-triaged: ${n.triage_label || n.triage_bucket}`}
                          style={{
                            fontSize: 9, padding: "1px 5px", borderRadius: 3,
                            background: "rgba(255,177,66,0.15)",
                            color: T.amber, border: `1px solid ${T.amber}`,
                            fontWeight: 600, letterSpacing: "0.05em",
                          }}
                        >🔕 {n.triage_bucket}</span>
                      )}
                    </div>
                    <div className="text-[11px] leading-snug mt-0.5" style={{ color: T.text }}>
                      {n.title}
                    </div>
                    {n.body && n.body !== n.title && (
                      <div className="text-[10px] mt-1 whitespace-pre-wrap"
                           style={{ color: T.textDim }}>
                        {n.body}
                      </div>
                    )}
                  </div>
                </div>
                <div className="flex flex-wrap gap-1 mt-2">
                  {(n.actions || []).includes("accept") && (
                    <button
                      data-testid={`inbox-accept-${n.id}`}
                      onClick={() => act(n.id, "accept")}
                      className="text-[10px] px-2 py-1 font-bold uppercase tracking-wider"
                      style={{ background: T.cyan, color: T.bg, borderRadius: 4 }}
                    >Yes fix it</button>
                  )}
                  {(n.actions || []).includes("reply") && (
                    <button
                      data-testid={`inbox-reply-${n.id}`}
                      onClick={() => setReplying(replying === n.id ? null : n.id)}
                      className="text-[10px] px-2 py-1 uppercase tracking-wider"
                      style={{ background: "transparent", border: `1px solid ${T.cyan}`, color: T.cyan, borderRadius: 4 }}
                    >Reply</button>
                  )}
                  <button
                    data-testid={`inbox-dismiss-${n.id}`}
                    onClick={() => act(n.id, "dismiss")}
                    className="text-[10px] px-2 py-1 uppercase tracking-wider"
                    style={{ background: "transparent", border: `1px solid ${T.textDim}`, color: T.textDim, borderRadius: 4 }}
                  >Dismiss</button>
                </div>
                {replying === n.id && (
                  <div className="mt-2 flex flex-col gap-1">
                    <div className="flex gap-1">
                      <input
                        data-testid={`inbox-reply-input-${n.id}`}
                        type="text"
                        value={replyText}
                        onChange={(e) => setReplyText(e.target.value)}
                        placeholder="Your reply…"
                        onKeyDown={(e) => { if (e.key === "Enter") act(n.id, "reply"); }}
                        className="flex-1 text-[11px] px-2 py-1"
                        style={{ background: "rgba(7,17,33,0.5)", border: `1px solid ${T.border}`, color: T.text, borderRadius: 4 }}
                        autoFocus
                      />
                      <button
                        data-testid={`inbox-reply-send-${n.id}`}
                        onClick={() => act(n.id, "reply")}
                        disabled={!replyText.trim()}
                        className="text-[10px] px-2 py-1 font-bold"
                        style={{ background: T.cyan, color: T.bg, borderRadius: 4, opacity: replyText.trim() ? 1 : 0.5 }}
                      >Send</button>
                    </div>
                    <button
                      data-testid={`inbox-draft-${n.id}`}
                      onClick={() => draftReply(n.id)}
                      disabled={drafting === n.id}
                      className="text-[10px] px-2 py-1 self-start uppercase tracking-wider"
                      style={{
                        background: "transparent",
                        border: `1px solid ${T.amber}`,
                        color: T.amber,
                        borderRadius: 4,
                        opacity: drafting === n.id ? 0.5 : 1,
                      }}
                    >{drafting === n.id ? "Drafting…" : "✨ AI Draft"}</button>
                  </div>
                )}
              </div>
            ))}
          </div>

          <TriageFooter T={T} />
          <PriorityFooter T={T} />
        </div>
      )}
    </DraggableFloater>
  );
}

// 🚨 Priority senders — inverse of triage. Matching senders get a
// louder alarm and are called out separately in the wake greeting.
function PriorityFooter({ T }) {
  const [open, setOpen] = useState(false);
  const [rules, setRules] = useState([]);
  const [pattern, setPattern] = useState("");
  const [field, setField] = useState("from");
  const load = async () => {
    try {
      const r = await api.get("/agent_inbox/priority_rules");
      setRules(r.data?.rules || []);
    } catch { /* ignore */ }
  };
  useEffect(() => { if (open) load(); }, [open]);
  const add = async () => {
    const p = pattern.trim().toLowerCase();
    if (!p) return;
    try {
      await api.post("/agent_inbox/priority_rules", { pattern: p, field, label: p });
      setPattern("");
      await load();
    } catch (e) { console.warn("[priority add]", e?.message); }
  };
  const remove = async (id) => {
    try {
      await api.delete(`/agent_inbox/priority_rules/${id}`);
      await load();
    } catch (e) { console.warn("[priority delete]", e?.message); }
  };
  return (
    <div style={{ borderTop: `1px solid ${T.border}`, padding: "6px 10px", background: "rgba(35,7,7,0.35)" }}>
      <button
        data-testid="inbox-priority-toggle"
        onClick={() => setOpen(v => !v)}
        className="text-[10px] uppercase tracking-wider w-full text-left"
        style={{ background: "transparent", border: "none", color: "#ef4444", cursor: "pointer" }}
      >{open ? "▾" : "▸"} 🚨 Priority senders · {rules.length || "none yet"}</button>
      {open && (
        <div className="mt-2 flex flex-col gap-2" data-testid="inbox-priority-panel">
          <div className="text-[10px]" style={{ color: T.textDim }}>
            Matching senders get an urgent shout instead of the normal whisper. Empty by default.
          </div>
          {rules.map(r => (
            <div key={r.id} data-testid={`priority-rule-${r.id}`} className="flex items-center gap-2 text-[10px]">
              <span style={{ color: T.textDim, minWidth: 40 }}>{r.field}</span>
              <span style={{ color: T.text, flex: 1, fontFamily: "monospace" }}>{r.pattern}</span>
              <button
                data-testid={`priority-rule-delete-${r.id}`}
                onClick={() => remove(r.id)}
                className="text-[10px] px-1"
                style={{ background: "transparent", border: "none", color: "#ef4444", cursor: "pointer" }}
              >✕</button>
            </div>
          ))}
          <div className="flex gap-1 mt-1">
            <select
              data-testid="priority-add-field"
              value={field}
              onChange={e => setField(e.target.value)}
              className="text-[10px] px-1"
              style={{ background: "rgba(7,17,33,0.6)", border: `1px solid ${T.border}`, color: T.text, borderRadius: 3 }}
            >
              <option value="from">from</option>
              <option value="subject">subject</option>
            </select>
            <input
              data-testid="priority-add-pattern"
              value={pattern}
              onChange={e => setPattern(e.target.value)}
              onKeyDown={e => { if (e.key === "Enter") add(); }}
              placeholder="e.g. accountant@"
              className="flex-1 text-[10px] px-2 py-1"
              style={{ background: "rgba(7,17,33,0.6)", border: `1px solid ${T.border}`, color: T.text, borderRadius: 3 }}
            />
            <button
              data-testid="priority-add-btn"
              onClick={add}
              disabled={!pattern.trim()}
              className="text-[10px] px-2 py-1"
              style={{ background: "#ef4444", color: "#fff", borderRadius: 3, fontWeight: 700, opacity: pattern.trim() ? 1 : 0.5 }}
            >Add</button>
          </div>
        </div>
      )}
    </div>
  );
}

// 👀 Auto-triage manager — expandable footer inside the inbox panel.
// Owner adds/removes match rules that silence known senders.
function TriageFooter({ T }) {
  const [open, setOpen] = useState(false);
  const [rules, setRules] = useState([]);
  const [pattern, setPattern] = useState("");
  const [field, setField] = useState("from");
  const load = async () => {
    try {
      const r = await api.get("/agent_inbox/triage_rules");
      setRules(r.data?.rules || []);
    } catch { /* ignore */ }
  };
  useEffect(() => { if (open) load(); }, [open]);
  const add = async () => {
    const p = pattern.trim().toLowerCase();
    if (!p) return;
    try {
      await api.post("/agent_inbox/triage_rules", { pattern: p, field });
      setPattern("");
      await load();
    } catch (e) { console.warn("[triage add]", e?.message); }
  };
  const remove = async (id) => {
    try {
      await api.delete(`/agent_inbox/triage_rules/${id}`);
      await load();
    } catch (e) { console.warn("[triage delete]", e?.message); }
  };
  return (
    <div style={{ borderTop: `1px solid ${T.border}`, padding: "6px 10px", background: "rgba(7,17,33,0.35)" }}>
      <button
        data-testid="inbox-triage-toggle"
        onClick={() => setOpen(v => !v)}
        className="text-[10px] uppercase tracking-wider w-full text-left"
        style={{ background: "transparent", border: "none", color: T.amber, cursor: "pointer" }}
      >{open ? "▾" : "▸"} 👀 Auto-triage · {rules.length || "…"} silenced senders</button>
      {open && (
        <div className="mt-2 flex flex-col gap-2" data-testid="inbox-triage-panel">
          <div className="text-[10px]" style={{ color: T.textDim }}>
            Silences the &quot;are you there?&quot; whisper for matching senders. They still appear here as info.
          </div>
          {rules.map(r => (
            <div key={r.id} data-testid={`triage-rule-${r.id}`} className="flex items-center gap-2 text-[10px]">
              <span style={{ color: T.textDim, minWidth: 40 }}>{r.field}</span>
              <span style={{ color: T.text, flex: 1, fontFamily: "monospace" }}>{r.pattern}</span>
              <button
                data-testid={`triage-rule-delete-${r.id}`}
                onClick={() => remove(r.id)}
                className="text-[10px] px-1"
                style={{ background: "transparent", border: "none", color: T.amber, cursor: "pointer" }}
              >✕</button>
            </div>
          ))}
          <div className="flex gap-1 mt-1">
            <select
              data-testid="triage-add-field"
              value={field}
              onChange={e => setField(e.target.value)}
              className="text-[10px] px-1"
              style={{ background: "rgba(7,17,33,0.6)", border: `1px solid ${T.border}`, color: T.text, borderRadius: 3 }}
            >
              <option value="from">from</option>
              <option value="subject">subject</option>
            </select>
            <input
              data-testid="triage-add-pattern"
              value={pattern}
              onChange={e => setPattern(e.target.value)}
              onKeyDown={e => { if (e.key === "Enter") add(); }}
              placeholder="e.g. @stripe.com"
              className="flex-1 text-[10px] px-2 py-1"
              style={{ background: "rgba(7,17,33,0.6)", border: `1px solid ${T.border}`, color: T.text, borderRadius: 3 }}
            />
            <button
              data-testid="triage-add-btn"
              onClick={add}
              disabled={!pattern.trim()}
              className="text-[10px] px-2 py-1"
              style={{ background: T.amber, color: T.bg, borderRadius: 3, fontWeight: 700, opacity: pattern.trim() ? 1 : 0.5 }}
            >Add</button>
          </div>
        </div>
      )}
    </div>
  );
}
